import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { getAuthHeaders } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

// Web Push subscription lifecycle for the "Activar notificaciones" opt-in.
// Never runs automatically — every call here (permission prompt, SW register,
// subscribe) is user-gesture-triggered from the UI, never on mount/login.
export function usePushNotifications() {
  const isSupported = typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && typeof Notification !== "undefined";
  const [permission, setPermission] = useState(isSupported ? Notification.permission : "unsupported");
  const [subscribing, setSubscribing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isSupported) return;
    // Reflect an already-granted permission from a previous session (no prompt fired here).
    setPermission(Notification.permission);
  }, [isSupported]);

  const subscribe = useCallback(async () => {
    if (!isSupported) { setError("not_supported"); return false; }
    setSubscribing(true);
    setError("");
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") return false;

      const { data } = await axios.get(`${API}/push/vapid-public-key`);
      const registration = await navigator.serviceWorker.register("/service-worker.js");
      await navigator.serviceWorker.ready;

      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.public_key),
        });
      }
      const json = subscription.toJSON();
      await axios.post(`${API}/push/subscribe`, json, { headers: getAuthHeaders() });
      return true;
    } catch (e) {
      setError(e?.response?.status === 503 ? "not_configured" : "subscribe_failed");
      return false;
    } finally {
      setSubscribing(false);
    }
  }, [isSupported]);

  // Auto-repair: permission already "granted" (from a previous session) but
  // the subscription itself is missing (cleared browser data, backend never
  // got it, etc.) — silently recreate it. Notification.requestPermission()
  // is a no-op (no prompt) when permission is already decided, so this never
  // surprises the user with a popup.
  useEffect(() => {
    if (!isSupported || permission !== "granted") return;
    (async () => {
      try {
        const registration = await navigator.serviceWorker.register("/service-worker.js");
        await navigator.serviceWorker.ready;
        const existing = await registration.pushManager.getSubscription();
        if (!existing) await subscribe();
      } catch { /* best-effort repair — never blocks the app */ }
    })();
  }, [isSupported, permission, subscribe]);

  return { isSupported, permission, subscribe, subscribing, error };
}
